import { Link } from 'react-router-dom'
import { formatRelative } from '../../lib/format'
import {
  filterLedgerRows, impactLine, impactWords, isBandAid, isRoot, kindCounts, kindLabel,
  LEVEL_FILTER_WORDS, LEVEL_TONE, LEVEL_WORDS, STATUS_TONE, STATUS_WORDS, type LevelFilter,
} from '../../lib/improve'
import type { CorrectionStatus } from '../../types/corrections'
import type { LedgerRow, LedgerSummary, ResolutionLevel } from '../../types/improve'

// The self-learning ledger: every correction, what was said, and — the two things the owner asked for —
// WHERE it was addressed (only this record, a rule for every case like it, fixed in the code, or waiting on
// a person) and WHAT ELSE that moved. Filtering is pure (lib/improve), so what the screen shows is provable
// without a browser. A band-aid is tinted and named as one; it must never sit here looking like a root fix.

export type StatusFilter = CorrectionStatus | 'all'

const STATUSES: StatusFilter[] = ['all', 'open', 'needs-human', 'improved', 'declined']
const FILTER_WORDS: Record<StatusFilter, string> = {
  all: 'any status', open: 'waiting for tonight', 'needs-human': 'need you', improved: 'improved', declined: 'declined',
}
const LEVELS: LevelFilter[] = ['all', 'open', 'band-aid', 'addressed']

export function Ledger({ rows, summary, level, status, kind, onLevel, onStatus, onKind, onOpen, openId }: {
  rows: LedgerRow[]
  summary: LedgerSummary
  level: LevelFilter
  status: StatusFilter
  kind: string
  onLevel: (level: LevelFilter) => void
  onStatus: (status: StatusFilter) => void
  onKind: (kind: string) => void
  onOpen: (correctionId: string) => void
  openId: string | null
}) {
  const shown = filterLedgerRows(rows, level, status, kind)
  const corrections = rows.map((r) => r.correction)
  const kinds = kindCounts(corrections)
  const statusCount = (s: StatusFilter) => (s === 'all' ? rows.length : corrections.filter((c) => c.status === s).length)
  const levelCount = (l: LevelFilter) => (l === 'all' ? summary.total : l === 'open' ? summary.open + summary.needsHuman : l === 'band-aid' ? summary.bandAids : summary.atRoot)

  return (
    <div className="card ledger-card">
      <div className="card-head">
        <div>
          <h2>The ledger <span className="count">{shown.length}</span></h2>
          <p className="muted">
            Every correction anyone made to what Scout asserted, and where each one was addressed. A fix to one
            record is shown as exactly that; a rule for every case like it, or a change to the code, is what a
            from-scratch re-read of the mail would get right without being told.
          </p>
        </div>
      </div>

      <div className="ledger-summary" aria-label="The ledger in numbers">
        <div className="ledger-stat"><strong>{summary.total}</strong><span>corrections</span></div>
        <div className="ledger-stat ok"><strong>{summary.atRoot}</strong><span>addressed at the root</span></div>
        <div className={`ledger-stat ${summary.bandAids > 0 ? 'warn' : ''}`}><strong>{summary.bandAids}</strong><span>band-aids still standing</span></div>
        <div className="ledger-stat"><strong>{summary.open}</strong><span>open</span></div>
        <div className={`ledger-stat ${summary.needsHuman > 0 ? 'warn' : ''}`}><strong>{summary.needsHuman}</strong><span>waiting on a person</span></div>
      </div>

      <div className="ledger-filters">
        {LEVELS.map((l) => (
          <button key={l} className={`chip-filter ${level === l ? 'on' : ''}`} onClick={() => onLevel(l)}>
            <strong>{levelCount(l)}</strong> <span>{LEVEL_FILTER_WORDS[l]}</span>
          </button>
        ))}
        <span className="filter-sep" />
        {STATUSES.map((s) => (
          <button key={s} className={`chip-filter ${status === s ? 'on' : ''}`} onClick={() => onStatus(s)}>
            <strong>{statusCount(s)}</strong> <span>{FILTER_WORDS[s]}</span>
          </button>
        ))}
        {kinds.length > 1 && (
          <>
            <span className="filter-sep" />
            <button className={`chip-filter ${kind === 'all' ? 'on' : ''}`} onClick={() => onKind('all')}><span>every kind</span></button>
            {kinds.map((k) => (
              <button key={k.kind} className={`chip-filter ${kind === k.kind ? 'on' : ''}`} onClick={() => onKind(k.kind)}>
                <strong>{k.count}</strong> <span>{kindLabel(k.kind).toLowerCase()}</span>
              </button>
            ))}
          </>
        )}
      </div>

      {rows.length === 0 && (
        <div className="empty-note">
          <strong>Nothing on the ledger yet.</strong>
          <p className="muted">
            Every claim Scout makes — a stage, a company kind, who someone works for, a rival in a room, a next step
            you owe — carries a "Not right?" beside it. Press one and it lands here, with your reason, for tonight's run.
          </p>
        </div>
      )}
      {rows.length > 0 && shown.length === 0 && (
        <p className="muted">
          Nothing on the ledger is {LEVEL_FILTER_WORDS[level]}{status === 'all' ? '' : ` and ${FILTER_WORDS[status]}`}{kind === 'all' ? '' : ` for ${kindLabel(kind).toLowerCase()}`}.
        </p>
      )}

      <ul className="ledger-list">
        {shown.map((r) => <LedgerItem key={r.correction.correctionId} row={r} open={openId === r.correction.correctionId} onOpen={() => onOpen(r.correction.correctionId)} />)}
      </ul>
    </div>
  )
}

function LedgerItem({ row, open, onOpen }: { row: LedgerRow; open: boolean; onOpen: () => void }) {
  const c = row.correction
  const level = row.level as ResolutionLevel
  const tone = LEVEL_TONE[level] ?? STATUS_TONE[c.status]
  const words = row.levelWords || LEVEL_WORDS[level] || row.level
  return (
    <li className={`ledger-item tone-${tone} level-${row.level}${open ? ' open' : ''}`}>
      <button className="ledger-press" onClick={onOpen} aria-expanded={open}>
        <span className="ledger-top">
          <span className="tag scope">{kindLabel(c.entityKind)}</span>
          <strong className="ledger-subject">{c.entityLabel ?? c.entityId}</strong>
          <span className={`tag sig ${STATUS_TONE[c.status]}`}>{STATUS_WORDS[c.status]}</span>
        </span>
        <span className="ledger-change">
          {c.fromValue && <><span className="was">{c.fromValue}</span> <span className="arrow" aria-hidden="true">→</span> </>}
          <span className="now">{c.toValue}</span>
        </span>
        <span className="ledger-why">{c.reason}</span>
        <span className="ledger-meta muted small">
          {formatRelative(c.at)}
          {c.turnCount > 0 && ` · ${c.turnCount} turn${c.turnCount === 1 ? '' : 's'} of discussion`}
        </span>
      </button>

      <div className="ledger-addressed">
        <span className="ledger-addressed-label">Addressed</span>
        <span className={`tag level ${tone}`}>{words}</span>
        {isBandAid(row.level) && <span className="muted small">a band-aid: a re-read from scratch would only know about this one</span>}
        {isRoot(row.level) && row.level === 'pattern-rule' && row.rule && <span className="muted small">{row.rule.because}</span>}
        {row.level === 'code' && row.resolution?.note && <span className="muted small">{row.resolution.note}</span>}
        {row.level === 'needs-human' && <span className="muted small">nothing general could be proved from this one; say it as a rule, or make a different correction</span>}
        {(c.ticketId || row.resolution?.ticketId) && (
          <Link className="ledger-ticket" to={`/quality?ticket=${encodeURIComponent(c.ticketId ?? row.resolution!.ticketId!)}`}>
            ticket {c.ticketId ?? row.resolution!.ticketId}
          </Link>
        )}
      </div>

      {(row.rule || row.resolution) && (
        <details className="ledger-impact">
          <summary>
            <span className="ledger-addressed-label">Impact</span> {impactWords(row.impact.count)}
          </summary>
          {row.impact.count === 0 ? (
            <p className="muted small">No other record was re-filed because of it.</p>
          ) : (
            <ul className="ledger-impact-list">
              {row.impact.examples.map((e) => (
                <li key={`${e.entityKind}:${e.entityId}`}>
                  <span className="tag scope">{e.entityKind}</span> {impactLine(e)}
                </li>
              ))}
              {row.impact.more > 0 && <li className="muted">{row.impact.more} more</li>}
            </ul>
          )}
        </details>
      )}

      {row.nudge && (
        <p className="ledger-nudge">{row.nudge}</p>
      )}
    </li>
  )
}
